import App from "@/App.vue";
import "@/assets/styles/colors.scss";
import "@/assets/styles/global.scss";
import "@/assets/styles/tailwind.css";
import router from "@/router";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { IonicVue } from "@ionic/vue";
import "@ionic/vue/css/core.css";
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@tabler/icons-webfont/dist/tabler-icons.min.css";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";

const piniaInstance = createPinia();
piniaInstance.use(piniaPluginPersistedstate);

window.addEventListener("vite:preloadError", () => {
  console.error("Vite preload error detected, reloading the app...");
  const appSettingsStore = useAppSettingsStore();
  console.log("Handling vite preload error via store...");
  appSettingsStore.handleVitePreloadError();
});

window.addEventListener("error", (event) => {
  const errorMessage = event.message || "";
  const isModuleScriptError =
    errorMessage.includes("Expected a JavaScript-or-Wasm module script") ||
    errorMessage.includes("Strict MIME type checking is enforced for module scripts");

  if (isModuleScriptError) {
    console.error("Module script MIME type error detected - likely expired version");
    window.location.href = "/version-expired.html";
    return;
  }
});

const app = createApp(App);
// `vue-mathjax-next` is intentionally NOT registered globally — `CustomLatexRenderer`
// imports it locally and lazy-loads the MathJax CDN script only when math is on screen.
app.use(IonicVue, { mode: "ios" }).use(piniaInstance).use(router);

app.config.errorHandler = (err, instance, info) => {
  console.error("Unhandled Vue error:", err, "\nComponent:", instance, "\nInfo:", info);
};

// Swiper 12's Vue wrapper forwards every internal Swiper event through emit(), but its
// `emits` array omits some underscore-prefixed internal events — e.g. `_freeModeStaticRelease`,
// fired on a free-mode drag-release — which produces a noisy dev-only Vue warning on swipe.
// Underscore-prefixed emitted-event names are a library-internal convention that app code
// never listens to, so filter exactly those and pass every other warning through unchanged.
app.config.warnHandler = (msg, _instance, trace) => {
  if (/emitted event "_/.test(msg)) {
    return;
  }
  console.warn(`[Vue warn]: ${msg}${trace}`);
};

// MSW activation (dev-only). `import.meta.env.DEV` is statically false in every production /
// staging build, so Rollup tree-shakes the dynamic import — dist/ never ships MSW. Within the
// dev server, the worker starts when VITE_USE_MSW=true (`npm run test:mock`) or `?msw=1` is in the
// URL (Playwright E2E). See plans/testing-framework component-map.md § A-3.
async function enableMockingThenMount() {
  if (
    import.meta.env.DEV &&
    (import.meta.env.VITE_USE_MSW === "true" || window.location.search.includes("msw=1"))
  ) {
    const { worker } = await import("../tests/msw/browser");
    await worker.start({ onUnhandledRequest: "warn" });
  }
  app.mount("#app");
}

enableMockingThenMount();
